import { z } from 'zod';
import { DomainError } from '../errors.js';
import { DISPOSAL_REASONS, FEFO_OVERRIDE, MOVEMENT_REASON_CODES } from '../provisions/lots.js';
import type { ParseResult } from '../voyage/schemas.js';
import { toIssues } from './catalog.js';

/**
 * Gemi stoğu (ADR 0026 §1, §2). Stok bir defterdir: eldeki miktar saklanmaz, değiştirilemez
 * hareketlerin toplamından hesaplanır. Yanlış hareket gerekçeli ters hareketle düzeltilir; PMS iş
 * kaydıyla aynı ilke [NR496 4.6].
 */

/**
 * Hareket türleri; `transfer` depo transferiyle sonra gelir (ADR 0026 §6). `dispose` imha/zayi:
 * gerekçe ve neden kodu zorunlu (ADR 0099 §8).
 */
export const STOCK_MOVEMENT_KINDS = ['in', 'out', 'adjust', 'receipt', 'dispose'] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

/** Miktarın işareti: giriş ve mal kabulü artı, çıkış eksi, düzeltme iki yönde. */
const SIGN: Readonly<Record<StockMovementKind, 1 | -1 | 0>> = {
  in: 1,
  out: -1,
  adjust: 0,
  receipt: 1,
  dispose: -1,
};

/** Toplamda kayan nokta artığı kalmasın diye (0,1 + 0,2 − 0,3 = 0). */
const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const MAX_QTY = 1_000_000_000;
const MAX_PRICE = 1_000_000_000;
const level = () =>
  z.number(typed('INVALID_NUMBER')).min(0, 'OUT_OF_RANGE').max(MAX_QTY, 'OUT_OF_RANGE');
const blankToNull = (value: string | null | undefined) => {
  const trimmed = value?.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed : null;
};

/** Stok kalemi: gemi × malzeme; yer, en az, en çok ve yeniden sipariş seviyesi. */
export const stockItemSchema = z
  .object({
    materialId: z.uuid(typed('INVALID_ID')),
    location: z.string(typed('INVALID_TEXT')).max(200, 'TOO_LONG').nullish().transform(blankToNull),
    minQty: level(),
    maxQty: level().nullish(),
    reorderLevel: level().nullish(),
  })
  .superRefine((item, ctx) => {
    if (item.maxQty !== undefined && item.maxQty !== null && item.minQty > item.maxQty) {
      ctx.addIssue({ code: 'custom', message: 'MIN_ABOVE_MAX', path: ['maxQty'] });
    }
  });
export type StockItem = z.output<typeof stockItemSchema>;

export function parseStockItem(input: unknown): ParseResult<StockItem> {
  const result = stockItemSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Stok hareketi. Gemi tarihi ve saat farkı kayıt katmanında gemi sunucusu saatinden yazılır. */
export const stockMovementSchema = z
  .object({
    stockItemId: z.uuid(typed('INVALID_ID')),
    kind: z.enum(STOCK_MOVEMENT_KINDS, typed('INVALID_OPTION')),
    quantity: z
      .number(typed('INVALID_NUMBER'))
      .min(-MAX_QTY, 'OUT_OF_RANGE')
      .max(MAX_QTY, 'OUT_OF_RANGE'),
    reason: z.string(typed('INVALID_TEXT')).max(500, 'TOO_LONG').nullish().transform(blankToNull),
    sourceType: z
      .string(typed('INVALID_TEXT'))
      .max(60, 'TOO_LONG')
      .nullish()
      .transform(blankToNull),
    sourceId: z.uuid(typed('INVALID_ID')).nullish(),
    /**
     * Birim fiyat ve para birimi (ADR 0047 §5): mal kabulünde PO satırından; ikisi birlikte dolu
     * ya da birlikte boş. Elle giriş ve çıkış fiyatsızdır.
     */
    unitPrice: z
      .number(typed('INVALID_NUMBER'))
      .min(0, 'OUT_OF_RANGE')
      .max(MAX_PRICE, 'OUT_OF_RANGE')
      .nullish()
      .transform((value) => value ?? null),
    currency: z
      .string(typed('INVALID_CURRENCY'))
      .regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY')
      .nullish()
      .transform((value) => value ?? null),
    /** Kumanya lotu (ADR 0099 §5); lotsuz hareket eski ve lot izlenmeyen kalemlerdir. */
    lotId: z
      .uuid(typed('INVALID_ID'))
      .nullish()
      .transform((value) => value ?? null),
    /** İmha nedeni ya da FEFO sapması (ADR 0099 §7, §8). */
    reasonCode: z
      .enum(MOVEMENT_REASON_CODES, typed('INVALID_OPTION'))
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((movement, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', message, path: [path] });
    const sign = SIGN[movement.kind];
    if (movement.quantity === 0) issue('quantity', 'ZERO_QUANTITY');
    else if (sign !== 0 && Math.sign(movement.quantity) !== sign)
      issue('quantity', 'QUANTITY_SIGN');
    if (movement.kind === 'adjust' && movement.reason === null) issue('reason', 'REQUIRED');
    if (movement.kind === 'dispose') {
      if (movement.reason === null) issue('reason', 'REQUIRED');
      if (!(DISPOSAL_REASONS as readonly unknown[]).includes(movement.reasonCode)) {
        issue('reasonCode', movement.reasonCode === null ? 'REQUIRED' : 'INVALID_OPTION');
      }
    } else if (movement.reasonCode !== null) {
      // FEFO sapması yalnız gerekçeli çıkışta (ADR 0099 §7)
      const fefo = movement.kind === 'out' && movement.reasonCode === FEFO_OVERRIDE;
      if (!fefo) issue('reasonCode', 'INVALID_OPTION');
      else if (movement.reason === null) issue('reason', 'REQUIRED');
    }
    // Mal kabulü GRN satırından doğar (ADR 0026 §1); kaynaksız kabul yok (en sıkı okuma)
    const sourceRequired = movement.kind === 'receipt';
    const hasType = movement.sourceType !== null;
    const hasId = !missing(movement.sourceId);
    if (!hasType && (sourceRequired || hasId)) issue('sourceType', 'REQUIRED');
    if (!hasId && (sourceRequired || hasType)) issue('sourceId', 'REQUIRED');
    if (movement.unitPrice !== null && movement.currency === null) issue('currency', 'REQUIRED');
    if (movement.unitPrice === null && movement.currency !== null) issue('unitPrice', 'REQUIRED');
  });
export type StockMovement = z.output<typeof stockMovementSchema>;

export function parseStockMovement(input: unknown): ParseResult<StockMovement> {
  const result = stockMovementSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export interface StockPrice {
  unitPrice: number;
  currency: string;
}

/** Fiyat hesabına giren hareket alanları. */
export interface PricedMovement {
  id: string;
  kind: string;
  quantity: number;
  createdAt: string;
  unitPrice: number | null;
  currency: string | null;
  sourceType: string | null;
  sourceId: string | null;
}

/**
 * Son alış fiyatı (ADR 0047 §5): ters hareketi olmayan (GRN'si iptal edilmemiş) en son fiyatlı
 * mal kabulü. Ters hareket aynı kaynağa (GRN satırı) bağlı eksi düzeltmedir.
 */
export function lastPrice(movements: readonly PricedMovement[]): StockPrice | null {
  const reversed = new Set(
    movements
      .filter((m) => m.kind === 'adjust' && m.quantity < 0 && m.sourceId !== null)
      .map((m) => `${m.sourceType}:${m.sourceId}`),
  );
  const receipts = movements
    .filter(
      (m) =>
        m.kind === 'receipt' &&
        m.unitPrice !== null &&
        m.currency !== null &&
        !reversed.has(`${m.sourceType}:${m.sourceId}`),
    )
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.id.localeCompare(b.id));
  const last = receipts.at(-1);
  return last ? { unitPrice: last.unitPrice as number, currency: last.currency as string } : null;
}

/** Stok değeri = eldeki × son fiyat, o para biriminde; fiyat yoksa bilinmez (kur yok). */
export function stockValue(
  quantity: number,
  price: StockPrice | null,
): { value: number; currency: string } | null {
  if (!price) return null;
  return { value: round(quantity * price.unitPrice), currency: price.currency };
}

/** Eldeki miktar: hareketlerin işaretli miktarlarının toplamı. */
export function onHand(movements: readonly { quantity: number }[]): number {
  return round(movements.reduce((sum, movement) => sum + movement.quantity, 0));
}

/**
 * Hareketten sonraki eldeki miktar. Stok sıfırın altına düşemez; düzeltme de dahil (ADR 0026 §2,
 * en sıkı okuma). Düşüyorsa `NEGATIVE_STOCK`.
 */
export function checkMovement(
  current: number,
  movement: { kind: StockMovementKind; quantity: number },
): number {
  const next = round(current + movement.quantity);
  if (next < 0) {
    throw new DomainError('NEGATIVE_STOCK', `eldeki ${current}, hareket ${movement.quantity}`);
  }
  return next;
}

export const STOCK_STATUSES = ['normal', 'low', 'critical'] as const;
export type StockStatus = (typeof STOCK_STATUSES)[number];

/**
 * Stok durumu (`satin-alma-akisi.jpeg` "Stock Management" Status sütunu):
 * - `low`: en az seviyede ya da altında (tasarımda eldeki 1, en az 1 olan kalem "Low Stock");
 * - `critical`: sıfır, en az seviyenin yarısının altında ya da kritik malzeme en az seviyede veya
 *   altında. Müşteri "Critical" ölçütünü vermedi; ajan kararı (ADR 0030) `docs/sorular.md` #54. Sıfır stok, en
 *   az seviyesi 0 olan kalemde de kritiktir (en sıkı okuma; CLAUDE.md "Çalışma şekli" 5).
 */
export function stockStatus(
  current: number,
  minQty: number,
  criticalMaterial = false,
): StockStatus {
  if (current <= 0 || current < minQty / 2) return 'critical';
  if (minQty > 0 && current <= minQty) return criticalMaterial ? 'critical' : 'low';
  return 'normal';
}
